import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CoachCard, CoachMessage } from '../../shared/api';
import { streamCoach } from './client';
import { qk } from './hooks';

export interface StreamingTurn {
  user: string;
  text: string;
  status: string | null;
  cards: CoachCard[];
  error: string | null;
}

/** Streams a Coach reply and folds the finished turn into the React Query cache. */
export function useCoachChat(today: string) {
  const client = useQueryClient();
  const [turn, setTurn] = useState<StreamingTurn | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const send = useCallback(
    async (message: string) => {
      const text = message.trim();
      if (!text || abortRef.current) return;
      const controller = new AbortController();
      abortRef.current = controller;
      setTurn({ user: text, text: '', status: 'Thinking…', cards: [], error: null });
      const userMsg: CoachMessage = { id: `local-${Date.now()}`, role: 'user', text, cards: [], safety: false, createdAt: new Date().toISOString() };
      let finished = false;
      try {
        await streamCoach(
          text,
          today,
          (event) => {
            if (event.type === 'status') setTurn((t) => (t ? { ...t, status: event.text } : t));
            if (event.type === 'card') setTurn((t) => (t ? { ...t, cards: [...t.cards, event.card] } : t));
            if (event.type === 'text') setTurn((t) => (t ? { ...t, status: null, text: t.text + event.delta } : t));
            if (event.type === 'error') {
              finished = true;
              setTurn((t) => (t ? { ...t, status: null, error: event.message } : t));
            }
            if (event.type === 'done') {
              finished = true;
              client.setQueryData<CoachMessage[]>(qk.coach, (prev) => [...(prev ?? []), userMsg, event.message]);
              setTurn(null);
            }
          },
          controller.signal,
        );
        if (!finished) setTurn((t) => (t && !t.error ? { ...t, status: null, error: 'Coach was interrupted before finishing. Try again.' } : t));
      } catch (err) {
        if (!(err instanceof DOMException && err.name === 'AbortError')) {
          setTurn((t) => (t ? { ...t, status: null, error: err instanceof Error ? err.message : 'Coach is unavailable.' } : t));
        }
      } finally {
        abortRef.current = null;
      }
    },
    [client, today],
  );

  const dismiss = useCallback(() => setTurn(null), []);
  return { turn, send, busy: turn !== null && !turn.error, dismiss };
}
